import SearchableMultiSelectDropdown from '@/common/SearchableMultiSelectDropdown/SearchableMultiSelectDropdown';
import PlayerModelsFilter from './PlayerModelsFilter';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { useCallback, useEffect, useState } from 'react';
import type { SetStateAction } from 'react';
import ThreeLinesMoreIcon from '@/assets/icons/three-lines-more.svg?react';
import ThreeLinesLessIcon from '@/assets/icons/three-lines-less.svg?react';
import RefreshIcon from '@/assets/icons/refresh.svg?react';
import MultiSelectDropdown, { MultiSelectOption } from '@/common/MultiSelectDropdown';
import { clubSupplyOptions, loadOrderFilterOptions } from '@/library/OrderFilterOptionsHelper';
import type { OrdersDashboardQuery } from './OrdersQuery';
import SearchTextFilter, { OrdersSearchCriteria, SearchMode } from './SearchTextFilter';
import styles from './OrdersFilters.module.scss';
import { ordersPresentation, OrdersPageType } from './OrdersPresentation';
import { CalendarDatePicker } from '@/common/DatePicker';
import { CalendarDateRangePicker } from '@/common/DatePicker/CalendarDateRangePicker';

interface IProps {
  onDefaults: () => void;
  currentPage: OrdersPageType;
  query: OrdersDashboardQuery;
  // eslint-disable-next-line no-unused-vars
  onFiltersChange: (update: SetStateAction<OrdersDashboardQuery>) => void;
}

export default function OrdersFilters({ currentPage, query, onFiltersChange, onDefaults }: IProps) {
  const isOnline = useOnlineStatus();
  const [showMore, setShowMore] = useState(false);
  const [statusOptions, setStatusOptions] = useState<MultiSelectOption[]>([]);
  const [buildLocationOptions, setBuildLocationOptions] = useState<MultiSelectOption[]>([]);
  const [accountOptions, setAccountOptions] = useState<MultiSelectOption[]>([]);
  const [tourRepOptions, setTourRepOptions] = useState<MultiSelectOption[]>([]);
  const [tournamentOptions, setTournamentOptions] = useState<MultiSelectOption[]>([]);
  // Tournament selection is provisional and must not trigger an order search.
  const [tournaments, setTournaments] = useState<MultiSelectOption[]>([]);
  const showTournament = currentPage === OrdersPageType.Clubs || currentPage === OrdersPageType.Putters;
  const presentation = ordersPresentation[currentPage];
  const [searchMode, setSearchMode] = useState<SearchMode>(presentation.defaultSearchMode);
  // Components has no Player mode to return to; empty number search must still permit filtering.
  const dropdownFiltersDisabled = presentation.showPlayer ? searchMode !== 'player' : Boolean(query.orderNo || query.laserNo);
  const setFilterQuery = (name: 'orderStatus' | 'buildLocation' | 'clubSupply' | 'account' | 'tourRep' | 'shipFrom' | 'shipTo', value: MultiSelectOption[]) => {
    onFiltersChange((currentQuery) => ({ ...currentQuery, [name]: value, offset: 0 }));
  };

  const handleSearchModeChange = useCallback(
    (mode: SearchMode) => {
      setSearchMode(mode);
      // TODO: Incorporate Tournaments into the API Query when implemented in the Create Order flow
      if (mode !== 'player') setTournaments([]);
      onFiltersChange((currentQuery) => {
        if (mode === 'player') {
          if (currentQuery.orderNo === undefined && currentQuery.laserNo === undefined && currentQuery.offset === 0) return currentQuery;
          return { ...currentQuery, orderNo: undefined, laserNo: undefined, offset: 0 };
        }

        const hasDropdownSelections =
          Boolean(currentQuery.requestedShipDate) ||
          Boolean(currentQuery.shipFrom?.length) ||
          Boolean(currentQuery.shipTo?.length) ||
          currentQuery.playerModels.length > 0 ||
          currentQuery.orderStatus.length > 0 ||
          currentQuery.buildLocation.length > 0 ||
          currentQuery.clubSupply.length > 0 ||
          currentQuery.account.length > 0 ||
          currentQuery.tourRep.length > 0;
        const hasCommittedSearch = currentQuery.playerId !== undefined || currentQuery.playerName !== undefined || currentQuery.orderNo !== undefined || currentQuery.laserNo !== undefined;
        if (!hasDropdownSelections && !hasCommittedSearch && currentQuery.offset === 0) return currentQuery;

        return {
          ...currentQuery,
          playerId: undefined,
          playerName: undefined,
          orderNo: undefined,
          laserNo: undefined,
          orderStatus: [],
          buildLocation: [],
          clubSupply: [],
          account: [],
          tourRep: [],
          playerModels: [],
          shipFrom: [],
          shipTo: [],
          requestedShipDate: undefined,
          offset: 0,
        };
      });
    },
    [onFiltersChange],
  );

  const handleSearchCommit = useCallback(
    (criteria: OrdersSearchCriteria) => {
      onFiltersChange((currentQuery) => {
        if (
          currentQuery.playerId === criteria.playerId &&
          currentQuery.playerName === criteria.playerName &&
          currentQuery.orderNo === criteria.orderNo &&
          currentQuery.laserNo === criteria.laserNo &&
          currentQuery.offset === 0
        )
          return currentQuery;

        return {
          ...currentQuery,
          playerModels: currentQuery.playerId === criteria.playerId ? currentQuery.playerModels : [],
          playerId: criteria.playerId,
          playerName: criteria.playerName,
          orderNo: criteria.orderNo,
          laserNo: criteria.laserNo,
          offset: 0,
        };
      });
    },
    [onFiltersChange],
  );

  useEffect(() => {
    const loadOptions = async () => {
      const options = await loadOrderFilterOptions(currentPage);
      setStatusOptions(options.orderStatuses);
      setBuildLocationOptions(options.buildLocations);
      setAccountOptions(options.orderAccounts);
      setTourRepOptions(options.appUsers);
      setTournamentOptions(options.tournaments);
    };
    loadOptions();
  }, [currentPage]);

  return (
    <>
      <div className={styles.row}>
        <div className={styles.rowItems}>
          <SearchTextFilter modes={presentation.searchModes} mode={searchMode} onModeChange={handleSearchModeChange} onCommit={handleSearchCommit} />
          <>
            <MultiSelectDropdown
              label="Order Status"
              options={statusOptions}
              value={query.orderStatus}
              onChange={(options) => setFilterQuery('orderStatus', options)}
              className={styles.orderStatusDropdown}
              disabled={dropdownFiltersDisabled}
            />
            {presentation.showBuildLocation && (
              <MultiSelectDropdown
                label="Build Location"
                options={buildLocationOptions}
                value={query.buildLocation}
                onChange={(options) => setFilterQuery('buildLocation', options)}
                className={styles.dropdownMedium}
                disabled={dropdownFiltersDisabled}
              />
            )}
          </>
          {presentation.tourRepInFirstRow && (
            <SearchableMultiSelectDropdown
              label="Tour Rep"
              searchLabel="Search tour reps"
              dropdownWidth="100%"
              options={tourRepOptions}
              value={query.tourRep}
              onChange={(options) => setFilterQuery('tourRep', options)}
              className={styles.dropdownLarge}
              disabled={dropdownFiltersDisabled}
            />
          )}
          {[OrdersPageType.Clubs, OrdersPageType.Components].includes(currentPage) && (
            <MultiSelectDropdown
              label="Club / Supply"
              options={clubSupplyOptions}
              value={query.clubSupply}
              onChange={(options) => setFilterQuery('clubSupply', options)}
              className={styles.dropdownLarge}
              disabled={dropdownFiltersDisabled}
            />
          )}
          <button type="button" className={styles.icons} aria-label={showMore ? 'Show fewer filters' : 'Show more filters'} aria-expanded={showMore} onClick={() => setShowMore(!showMore)}>
            {!showMore ? (
              <>
                <ThreeLinesMoreIcon className={styles.icons} />
                <span>More</span>
              </>
            ) : (
              <>
                <ThreeLinesLessIcon className={styles.icons} />
                <span>Less</span>
              </>
            )}
          </button>
          <button type="button" className={`${styles.icons} ${styles.defaultIcon}`} aria-label="Load default filters" onClick={onDefaults}>
            <RefreshIcon className={styles.icons} />
            <span>Defaults</span>
          </button>
        </div>
        {/*Show More row */}
        <div className={styles.expandedRow} hidden={!showMore}>
          {showMore && (
            <>
              <fieldset className={styles.dateFilter} disabled={dropdownFiltersDisabled}>
                <legend>Order Date</legend>
                <CalendarDateRangePicker
                  label="Order Date"
                  fromDate={query.orderDateFrom}
                  toDate={query.orderDateTo}
                  disabled={dropdownFiltersDisabled}
                  onSelectFromDate={(orderDateFrom) => onFiltersChange((current) => ({ ...current, orderDateFrom, offset: 0 }))}
                  onSelectToDate={(orderDateTo) => onFiltersChange((current) => ({ ...current, orderDateTo, offset: 0 }))}
                />
                {query.orderDateFrom && (
                  <button
                    type="button"
                    aria-label="Clear requested ship date"
                    onClick={() => onFiltersChange((current) => ({ ...current, orderDateFrom: undefined, orderDateTo: undefined, offset: 0 }))}
                  >
                    ×
                  </button>
                )}
              </fieldset>
              <fieldset className={styles.dateFilter} disabled={dropdownFiltersDisabled}>
                <legend>Req. Ship Date</legend>
                <CalendarDatePicker
                  label="Req. Ship Date"
                  value={query.requestedShipDate}
                  disabled={dropdownFiltersDisabled}
                  onSelect={(requestedShipDate) => onFiltersChange((current) => ({ ...current, requestedShipDate, offset: 0 }))}
                />
                {query.requestedShipDate && (
                  <button type="button" aria-label="Clear requested ship date" onClick={() => onFiltersChange((current) => ({ ...current, requestedShipDate: undefined, offset: 0 }))}>
                    ×
                  </button>
                )}
              </fieldset>
              <MultiSelectDropdown
                label="Account"
                options={accountOptions}
                value={query.account}
                onChange={(options) => setFilterQuery('account', options)}
                className={styles.dropdownLarge}
                disabled={dropdownFiltersDisabled}
              />
              {!presentation.tourRepInFirstRow && (
                <SearchableMultiSelectDropdown
                  label="Tour Rep"
                  searchLabel="Search tour reps"
                  dropdownWidth="100%"
                  options={tourRepOptions}
                  value={query.tourRep}
                  onChange={(options) => setFilterQuery('tourRep', options)}
                  className={styles.dropdownLarge}
                  disabled={dropdownFiltersDisabled}
                />
              )}
              {showTournament && (
                <MultiSelectDropdown label="Tournament" options={tournamentOptions} value={tournaments} onChange={setTournaments} className={styles.dropdownLarge} disabled={dropdownFiltersDisabled} />
              )}
              {presentation.showShippingLocations && (
                <>
                  <MultiSelectDropdown
                    label="Ship From"
                    options={buildLocationOptions}
                    value={query.shipFrom ?? []}
                    onChange={(options) => setFilterQuery('shipFrom', options)}
                    className={styles.dropdownLarge}
                    disabled={dropdownFiltersDisabled}
                  />
                  <MultiSelectDropdown
                    label="Ship To"
                    options={buildLocationOptions}
                    value={query.shipTo ?? []}
                    onChange={(options) => setFilterQuery('shipTo', options)}
                    className={styles.dropdownLarge}
                    disabled={dropdownFiltersDisabled}
                  />
                </>
              )}
            </>
          )}
          {currentPage === OrdersPageType.Clubs && (
            <PlayerModelsFilter
              playerId={query.playerId}
              enabled={isOnline && currentPage === OrdersPageType.Clubs && searchMode === 'player'}
              visible={showMore}
              value={query.playerModels}
              onChange={(playerModels) => onFiltersChange((current) => ({ ...current, playerModels, offset: 0 }))}
              className={styles.dropdownLarge}
            />
          )}
        </div>
      </div>
    </>
  );
}
